import React, { useEffect, useState } from "react";
import { Plus, Trash2, X, Pencil, Wallet } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtINR } from "@/lib/api";

export default function AdminAgents() {
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [overview, setOverview] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", commission_rate: 5 });

  const load = async () => {
    const r = await api.get("/admin/agents");
    setList(r.data);
    try {
      const o = await api.get("/admin/agents-earnings-overview");
      const map = {};
      for (const x of o.data) map[x.agent_id] = x;
      setOverview(map);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.full_name || !form.phone) return toast.error("Name & phone required");
    try { await api.post("/admin/agents", { ...form, commission_rate: parseFloat(form.commission_rate) }); toast.success("Agent added"); setShowAdd(false); load(); setForm({ full_name: "", phone: "", email: "", commission_rate: 5 }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete agent?")) return;
    await api.delete(`/admin/agents/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-agents-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Agents</h1>
          <p className="text-sm text-slate-500">{list.length} active agents</p>
        </div>
        <button data-testid="open-add-agent" onClick={() => setShowAdd(true)} className="inline-flex items-center gap-2 bg-tenant-primary text-white px-4 py-2.5 rounded-xl text-sm font-bold tap-scale">
          <Plus className="w-4 h-4" /> Add Agent
        </button>
      </div>

      <div className="mt-4 bg-white border border-slate-100 rounded-2xl card-shadow table-scroll">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
            <tr>
              <th className="text-left p-3">Agent / ID</th>
              <th className="text-left p-3">Phone</th>
              <th className="text-right p-3">Customers</th>
              <th className="text-right p-3">Earned</th>
              <th className="text-right p-3">Paid</th>
              <th className="text-right p-3">Balance Due</th>
              <th className="text-center p-3">Status</th>
              <th className="text-right p-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.map((a) => {
              const ov = overview[a.id] || {};
              return (
                <tr key={a.id} data-testid={`agent-row-${a.id}`} className="hover:bg-slate-50">
                  <td className="p-3">
                    <div className="font-bold">{a.full_name}</div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-wider">{a.agent_code}</div>
                  </td>
                  <td className="p-3 text-slate-600">+91 {a.phone}</td>
                  <td className="p-3 text-right font-extrabold">{a.customer_count}</td>
                  <td className="p-3 text-right font-extrabold text-emerald-600">{fmtINR(ov.total_earned ?? a.earnings ?? 0)}</td>
                  <td className="p-3 text-right font-bold text-blue-600">{fmtINR(ov.total_paid || 0)}</td>
                  <td className="p-3 text-right font-extrabold text-amber-600">{fmtINR(ov.balance_due || 0)}</td>
                  <td className="p-3 text-center">
                    <span className={`text-[10px] uppercase font-bold px-2.5 py-1 rounded-full ${a.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{a.status}</span>
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button data-testid={`earnings-agent-${a.id}`} onClick={() => nav(`/admin/agents/${a.id}/earnings`)} className="text-[11px] font-bold text-emerald-600 mr-3 inline-flex items-center gap-1"><Wallet className="w-3 h-3" /> Earnings</button>
                    <button data-testid={`edit-agent-${a.id}`} onClick={() => setShowEdit(a)} className="text-[11px] font-bold text-blue-600 mr-3 inline-flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>
                    <button data-testid={`del-agent-${a.id}`} onClick={() => remove(a.id)} className="text-rose-500"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              );
            })}
            {list.length === 0 && <tr><td colSpan={8} className="text-center p-6 text-slate-400">No agents yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur grid place-items-center z-50 p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3">
              <div className="font-extrabold text-lg">Add Agent</div>
              <button onClick={() => setShowAdd(false)}><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              {[
                ["full_name", "Full Name *", "text"], ["phone", "Mobile *", "text"],
                ["email", "Email", "email"], ["commission_rate", "Commission %", "number"],
              ].map(([k, l, t]) => (
                <div key={k}>
                  <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">{l}</label>
                  <input data-testid={`add-agent-${k}`} type={t} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none" />
                </div>
              ))}
              <button data-testid="confirm-add-agent" onClick={add} className="w-full bg-tenant-primary text-white py-3 rounded-xl font-bold">Save</button>
            </div>
          </div>
        </div>
      )}

      {showEdit && (
        <EditAgentModal agent={showEdit} onClose={() => setShowEdit(null)} onDone={load} />
      )}
    </div>
  );
}

const EditAgentModal = ({ agent, onClose, onDone }) => {
  const [form, setForm] = useState({
    full_name: agent.full_name || "",
    email: agent.email || "",
    commission_rate: String(agent.commission_rate ?? 5),
    status: agent.status || "active",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.full_name) return toast.error("Name required");
    setSaving(true);
    try {
      await api.patch(`/admin/agents/${agent.id}`, {
        full_name: form.full_name,
        email: form.email || null,
        commission_rate: parseFloat(form.commission_rate),
        status: form.status,
      });
      toast.success("Agent updated");
      onClose(); onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur grid place-items-center z-50 p-4" onClick={onClose}>
      <div data-testid="edit-agent-modal" className="bg-white rounded-3xl p-6 max-w-sm w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-3">
          <div>
            <div className="font-extrabold text-lg">Edit Agent</div>
            <div className="text-[11px] text-slate-500">+91 {agent.phone} · {agent.agent_code}</div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Full Name *</label>
            <input data-testid="edit-agent-full_name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-tenant-primary" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Email</label>
            <input data-testid="edit-agent-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-tenant-primary" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Commission %</label>
            <input data-testid="edit-agent-commission_rate" type="number" step="0.1" value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: e.target.value })} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-tenant-primary" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Status</label>
            <select data-testid="edit-agent-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-tenant-primary bg-white">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>

          <button data-testid="confirm-edit-agent" onClick={save} disabled={saving} className="w-full bg-tenant-primary text-white py-3 rounded-xl font-bold disabled:opacity-60">
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};
